/**
 * Shared CSS styles for STEWARD widget components.
 */
import { css } from 'lit';

export const sharedStyles = css`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :host {
    font-family: var(--steward-font-family);
    font-size: var(--steward-font-size);
    line-height: var(--steward-line-height);
    color: var(--steward-text);
  }

  /* ── Custom tooltip ───────────────────────────────────────────────
   * CSS-only tooltip that replaces the native \`title\` attribute's
   * fixed ~500ms browser delay with a snappy ~150ms appear delay and
   * a glass chip that matches the widget aesthetic. Buttons opt in
   * with \`data-tip="..."\`; \`aria-label\` is kept separate so screen
   * readers still get the canonical accessible label.
   *
   * Default position: ABOVE the trigger. For triggers near the top of
   * a clipping container (e.g. header buttons inside the panel's
   * overflow:hidden box) add \`data-tip-pos="below"\` to flip.
   *
   * Anchored via \`position: absolute\` on the ::after, so the host
   * trigger needs \`position: relative\` — applied here to every
   * \`[data-tip]\` element so consumers don't have to remember. Setting
   * position:relative on a button is layout-neutral.
   *
   * Touch devices: \`:hover\` is "sticky" on tap. We accept that — a
   * single tap shows the tip, the next tap (anywhere else) dismisses
   * it. Not ideal but reasonable; \`@media (hover: hover)\` gating
   * would disable the feature for keyboard-on-touch users. */
  [data-tip] {
    position: relative;
  }
  [data-tip]::after {
    content: attr(data-tip);
    position: absolute;
    z-index: 100;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%) translateY(3px);
    padding: 4px 8px;
    border-radius: 4px;
    /* Tooltip mirrors a hovered button, but OPAQUE so text behind the
     * panel doesn't bleed through and garble it. The .header-btn:hover
     * tint is currentColor@18% over the panel; we reproduce the same
     * visual by mixing currentColor@18% against --steward-bg (which IS
     * the panel surface), giving an identical-looking but fully solid
     * chip.
     *   color  = currentColor (the button's own text color)
     *   border = none */
    background: color-mix(in srgb, currentColor 18%, var(--steward-bg));
    color: currentColor;
    border: none;
    font-family: var(--steward-font-family);
    font-size: 11px;
    font-weight: 500;
    line-height: 1.3;
    white-space: nowrap;
    letter-spacing: 0;
    text-transform: none;
    pointer-events: none;
    user-select: none;
    opacity: 0;
    transition:
      opacity 0.12s ease 0s,
      transform 0.12s ease 0s;
  }
  [data-tip]:hover::after,
  [data-tip]:focus-visible::after {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
    transition-delay: 0.15s;
  }
  [data-tip-pos='below']::after {
    bottom: auto;
    top: calc(100% + 6px);
    transform: translateX(-50%) translateY(-3px);
  }
  [data-tip-pos='below']:hover::after,
  [data-tip-pos='below']:focus-visible::after {
    transform: translateX(-50%) translateY(0);
  }

  /* Anchor variants for corner buttons whose centered tooltip would
   * overflow the panel's left / right boundary (panel has
   * overflow:hidden so the chip would otherwise get clipped). Use
   *   data-tip-anchor="start" → anchor from the button's left edge
   *   data-tip-anchor="end"   → anchor from the button's right edge
   * These compose with data-tip-pos (default above; "below" supported). */
  [data-tip-anchor='start']::after {
    left: 0;
    transform: translateX(0) translateY(3px);
  }
  [data-tip-anchor='start']:hover::after,
  [data-tip-anchor='start']:focus-visible::after {
    transform: translateX(0) translateY(0);
  }
  [data-tip-anchor='end']::after {
    left: auto;
    right: 0;
    transform: translateX(0) translateY(3px);
  }
  [data-tip-anchor='end']:hover::after,
  [data-tip-anchor='end']:focus-visible::after {
    transform: translateX(0) translateY(0);
  }
  /* When anchor + below combine, keep the y-offset behaviour from
   * data-tip-pos="below" (slide up into place) without disturbing
   * the left/right anchor. */
  [data-tip-pos='below'][data-tip-anchor='start']::after,
  [data-tip-pos='below'][data-tip-anchor='end']::after {
    transform: translateX(0) translateY(-3px);
  }
  [data-tip-pos='below'][data-tip-anchor='start']:hover::after,
  [data-tip-pos='below'][data-tip-anchor='start']:focus-visible::after,
  [data-tip-pos='below'][data-tip-anchor='end']:hover::after,
  [data-tip-pos='below'][data-tip-anchor='end']:focus-visible::after {
    transform: translateX(0) translateY(0);
  }
  /* Respect reduced-motion: drop the slide; keep the fade. */
  @media (prefers-reduced-motion: reduce) {
    [data-tip]::after {
      transform: translateX(-50%);
      transition: opacity 0.12s ease 0s;
    }
    [data-tip]:hover::after,
    [data-tip]:focus-visible::after {
      transform: translateX(-50%);
    }
  }
`;
